import React, { useEffect, useState, useMemo } from "react";
import Layout from "../Layout";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import SimpleWordCloud from "./SimpleWordCloud";
import { Story } from "../../types/StoryTypes";
import { analyticsService } from "../../services/analytics";
import "../../styles/analytics.css";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

const PublicAnalyticsDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [nlpInsights, setNlpInsights] = useState<any>(null); // Added nlpInsights state
  const [historicalSentiment, setHistoricalSentiment] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const getWordColor = (value: number) => {
    // Color scale based on value
    if (value > 10) return "#1f77b4";
    if (value > 5) return "#ff7f0e";
    if (value > 2) return "#2ca02c";
    return "#d62728";
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get approved stories
        const q = query(
          collection(db, "stories"),
          where("status", "==", "approved")
        );

        const querySnapshot = await getDocs(q);
        const stories = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Story[];

        // Generate basic report
        const report = generatePublicReport(stories);
        setData(report);

        // Fetch NLP insights from analytics service
        try {
          const publicAnalytics = await analyticsService.getPublicAnalytics();
          if (publicAnalytics && publicAnalytics.length > 0) {
            // Get the most recent analysis
            setNlpInsights(publicAnalytics[0]);
          }
        } catch (error) {
          console.error("Error fetching NLP insights:", error);
        }

        // Fetch historical sentiment data if available
        try {
          const historicalQuery = query(
            collection(db, "storyAnalytics"),
            where("isPublic", "==", true),
            orderBy("timestamp", "desc"),
            limit(5)
          );

          const historicalSnapshot = await getDocs(historicalQuery);

          if (!historicalSnapshot.empty) {
            // Extract sentiment data with timestamps
            const sentimentData = historicalSnapshot.docs.map((doc) => {
              const data = doc.data();
              const timestamp = data.timestamp?.toDate?.() || new Date();
              return {
                month: timestamp.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                }),
                sentiment: data.sentiment?.score || 0,
                date: timestamp,
              };
            });

            // Sort by date
            sentimentData.sort((a, b) => a.date.getTime() - b.date.getTime());
            setHistoricalSentiment(sentimentData);
          }
        } catch (error) {
          console.error("Error fetching historical sentiment:", error);
        }
      } catch (error) {
        console.error("Error fetching public analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Convert keyword data to format needed by ReactWordcloud
  const treatmentWords = useMemo(() => {
    if (!data?.keywordData) return [];

    return data.keywordData.map((item: { name: any; value: any }) => ({
      text: item.name,
      value: item.value,
    }));
  }, [data]);

  // Calculate sentiment over time using monthly data if historical isn't available
  const sentimentOverTime = useMemo(() => {
    // If we have historical sentiment data, use that
    if (historicalSentiment.length > 0) {
      return historicalSentiment;
    }

    // Otherwise, create a synthetic version using monthly trend data
    if (!data || !data.monthlyTrend) return [];

    // Calculate a pseudo-sentiment based on story volume
    // This is a fallback when real sentiment data isn't available
    const maxStories = Math.max(
      ...data.monthlyTrend.map((m: { stories: any }) => m.stories || 0)
    );

    return data.monthlyTrend.map((month: { name: any; stories: any }) => ({
      month: month.name,
      sentiment: maxStories
        ? ((month.stories || 0) / maxStories) * 0.8 - 0.4
        : 0,
      // This creates a range between -0.4 and 0.4 based on relative volume
    }));
  }, [data, historicalSentiment]);

  // Generate analytics that only require approved stories data
  const generatePublicReport = (stories: Story[]) => {
    const totalStories = stories.length;
    const storiesWithXLinks = stories.filter((s) => !!s.xLink).length;

    // Get stories from the last 7 days
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    const recentStories = stories.filter((s) => {
      const createdAt = s.createdAt?.toDate
        ? s.createdAt.toDate()
        : new Date(s.createdAt);
      return createdAt > oneWeekAgo;
    });

    // Generate monthly trend data
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentYear = new Date().getFullYear();

    const monthlyData = months.map((month) => ({
      name: month,
      stories: 0,
    }));

    stories.forEach((story) => {
      const date = story.createdAt?.toDate
        ? story.createdAt.toDate()
        : new Date(story.createdAt);
      if (date && date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        monthlyData[monthIndex].stories += 1;
      }
    });

    // Generate keyword frequency data from story texts
    const keywordMap: Record<string, number> = {};
    const relevantKeywords = [
      "diet",
      "nutrition",
      "exercise",
      "meditation",
      "supplement",
      "vitamin",
      "therapy",
      "alternative",
      "natural",
      "holistic",
      "treatment",
      "recovery",
      "healing",
      "hope",
      "journey",
    ];

    stories.forEach((story) => {
      const lowerText = story.text.toLowerCase();
      relevantKeywords.forEach((keyword) => {
        if (lowerText.includes(keyword)) {
          keywordMap[keyword] = (keywordMap[keyword] || 0) + 1;
        }
      });
    });

    const keywordData = Object.entries(keywordMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return {
      totalStories,
      storiesWithXLinks,
      xLinkPercentage: totalStories
        ? (storiesWithXLinks / totalStories) * 100
        : 0,
      recentStoriesCount: recentStories.length,
      monthlyTrend: monthlyData,
      keywordData,
    };
  };
  if (loading) {
    return (
      <Layout title="Analytics Dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Community Analytics">
      <div className="analytics-container">
        <div className="analytics-header">
          <h2>Cancer Treatment Insights</h2>
          <p className="subtitle">
            Exploring patterns in alternative treatment approaches shared by our
            community
          </p>
        </div>

        {/* Basic metrics section */}
        <div className="metrics-summary">
          {data && (
            <>
              <div className="metric-card">
                <h3>Total Stories</h3>
                <div className="metric-value">{data.totalStories || 0}</div>
                <div className="metric-description">Shared experiences</div>
              </div>
              <div className="metric-card">
                <h3>X Links</h3>
                <div className="metric-value">
                  {data.storiesWithXLinks || 0}
                </div>
                <div className="metric-description">
                  Stories with X references
                </div>
              </div>
              <div className="metric-card">
                <h3>Stories (Last 7 Days)</h3>
                <div className="metric-value">
                  {data.recentStoriesCount || 0}
                </div>
                <div className="metric-description">Recent activity</div>
              </div>
            </>
          )}
        </div>

        {/* Charts section */}
        <div className="charts-grid">
          {/* Monthly Story Submissions */}
          {data?.monthlyTrend && data.monthlyTrend.length > 0 && (
            <div className="chart-container">
              <h3>Monthly Story Submissions</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="stories"
                    stroke="#8884d8"
                    name="Stories"
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* X Link Integration */}
          {data && (
            <div className="chart-container">
              <h3>X Link Integration</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "With X Links",
                        value: data.storiesWithXLinks || 0,
                      },
                      {
                        name: "Text Only",
                        value:
                          (data.totalStories || 0) -
                          (data.storiesWithXLinks || 0),
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.slice(0, 2).map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Word Cloud */}
          {data?.keywordData && data.keywordData.length > 0 && (
            <div className="chart-container wide">
              <h3>Treatment Effectiveness Word Cloud</h3>
              <p className="chart-description">
                Common terms used when discussing treatment effectiveness
              </p>
              <SimpleWordCloud words={treatmentWords} />
            </div>
          )}

          {/* Sentiment Over Time */}
          {sentimentOverTime.length > 0 && (
            <div className="chart-container">
              <h3>Sentiment Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sentimentOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[-1, 1]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sentiment"
                    stroke="#8884d8"
                    dot={true}
                    name="Sentiment Score"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* NLP Insights Section */}
        {nlpInsights && (
          <div className="nlp-insights-section">
            <h2>NLP Analysis Insights</h2>
            <p className="subtitle">
              Patterns extracted from story content using natural language
              processing
            </p>

            {/* Entity Relationships Graph */}
            {nlpInsights.graphData &&
              nlpInsights.graphData.nodes?.length > 0 && (
                <div className="chart-container wide">
                  <h3>Entity Relationships</h3>
                  <p className="chart-description">
                    This graph shows relationships between treatments, symptoms,
                    and outcomes mentioned in stories.
                  </p>
                  <div
                    className="graph-placeholder"
                    style={{
                      height: "300px",
                      background: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <p>
                      Interactive graph visualization would be displayed here
                    </p>
                  </div>
                </div>
              )}

            {/* Top Entities */}
            {nlpInsights.topEntities && nlpInsights.topEntities.length > 0 && (
              <div className="chart-container">
                <h3>Top Treatment Entities</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={nlpInsights.topEntities.slice(0, 8)}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="text" type="category" width={150} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Mentions" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sentiment Analysis */}
            {nlpInsights.sentiment && (
              <div className="chart-container">
                <h3>Overall Sentiment</h3>
                <div className="sentiment-gauge">
                  <div
                    className="gauge-value"
                    style={{
                      textAlign: "center",
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color:
                        nlpInsights.sentiment.score > 0.2
                          ? "green"
                          : nlpInsights.sentiment.score < -0.2
                          ? "red"
                          : "gray",
                    }}
                  >
                    {nlpInsights.sentiment.score > 0.2
                      ? "Positive"
                      : nlpInsights.sentiment.score < -0.2
                      ? "Negative"
                      : "Neutral"}
                    <div style={{ fontSize: "1rem", fontWeight: "normal" }}>
                      Score: {nlpInsights.sentiment.score.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="analytics-footer">
          <p>
            This data represents publicly shared stories from our community. We
            respect privacy and only analyze approved, anonymous contributions.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default PublicAnalyticsDashboard;
